import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import api from '../services/api';
import { getListadoServiciosGeo } from '../services/azureApi';
import { useSocket } from '../context/SocketContext';
import { useAuth } from '../context/AuthContext';
import './Incidentes.css';

const COD_PRIORIDAD = { '1': 'baja', '2': 'baja', '3': 'media', '4': 'alta', '5': 'critica' };
const ini2Str = () => { const d = new Date(); d.setDate(d.getDate() - 2); return d.toISOString().split('T')[0]; };

const PRIORIDAD = {
  critica: { label: 'Crítica', bg: '#fde8e8', color: '#8e1010', border: '#c0392b' },
  alta:    { label: 'Alta',    bg: '#fde8e8', color: '#c0392b', border: '#e74c3c' },
  media:   { label: 'Media',   bg: '#fef3e8', color: '#b7770d', border: '#e67e22' },
  baja:    { label: 'Baja',    bg: '#eafaf1', color: '#1a7a3c', border: '#27ae60' },
};

const ESTADOS_RESP = {
  /* estados nuevos del móvil */
  en_camino_compania: { label: 'En camino a la compañía',                  emoji: '🚒', color: '#e67e22', bg: '#fff3e0' },
  en_compania:        { label: 'Ya llegué a la compañía',                  emoji: '🏠', color: '#27ae60', bg: '#eafaf1' },
  en_camino_escena:   { label: 'En camino a la escena con equipo completo', emoji: '🔥', color: '#c0392b', bg: '#fde8e8' },
  /* estados legacy (compatibilidad) */
  en_camino:     { label: 'En Camino',     emoji: '🚒', color: '#e67e22', bg: '#fff3e0' },
  en_escena:     { label: 'En Escena',     emoji: '🔥', color: '#c0392b', bg: '#fde8e8' },
  disponible:    { label: 'Disponible',    emoji: '✅', color: '#27ae60', bg: '#eafaf1' },
  no_disponible: { label: 'No Disponible', emoji: '🚫', color: '#7f8c8d', bg: '#f0f0f0' },
  llegare_tarde: { label: 'Llegaré Tarde', emoji: '⏳', color: '#8e44ad', bg: '#f5eef8' },
};

const initials = (str) =>
  (str || '?').trim().split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase();

/* ── Fila de respuesta de bombero ─────────────────────────────────────────── */
function BomberoRow({ r }) {
  const cfg = ESTADOS_RESP[r.estado] || { label: r.estado, emoji: '❓', color: '#6c757d', bg: '#f5f5f5' };
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12,
      padding: '12px 16px',
      background: '#fafbfc',
      borderRadius: 10,
      border: '1px solid #e9ecef',
    }}>
      {/* Avatar sólido */}
      <div style={{
        width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
        background: cfg.color,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: '#fff', fontWeight: 800, fontSize: 13,
        boxShadow: `0 2px 6px ${cfg.color}55`,
      }}>
        {initials(r.nombre)}
      </div>

      {/* Nombre y carnet */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#1a1a2e', lineHeight: 1.3 }}>
          {r.nombre || '—'}
        </div>
        {r.carnet && r.carnet !== r.nombre && (
          <div style={{ fontSize: 12, color: '#868e96', marginTop: 2 }}>
            🪪 Carnet: <strong style={{ color: '#495057' }}>{r.carnet}</strong>
          </div>
        )}
      </div>

      {/* Estado badge sólido */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6,
        background: cfg.color,
        color: '#fff',
        borderRadius: 8,
        padding: '6px 14px',
        fontSize: 12, fontWeight: 700, flexShrink: 0,
        boxShadow: `0 2px 6px ${cfg.color}55`,
      }}>
        <span style={{ fontSize: 14 }}>{cfg.emoji}</span>
        {cfg.label}
      </div>
    </div>
  );
}

BomberoRow.propTypes = {
  r: PropTypes.shape({
    id:     PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    nombre: PropTypes.string,
    carnet: PropTypes.string,
    estado: PropTypes.string,
  }).isRequired,
};

/* ── Modal de confirmación ───────────────────────────────────────────────── */
function ConfirmModal({ titulo, onConfirm, onCancel, loading }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(15,23,42,.55)',
      backdropFilter: 'blur(4px)',
      animation: 'fadeIn .15s ease',
    }}>
      <div style={{
        background: '#fff',
        borderRadius: 18,
        padding: '32px 28px 24px',
        maxWidth: 420, width: '90%',
        boxShadow: '0 24px 60px rgba(0,0,0,.22)',
        textAlign: 'center',
        animation: 'slideUp .2s ease',
      }}>
        {/* Icono */}
        <div style={{
          width: 60, height: 60, borderRadius: '50%',
          background: 'linear-gradient(135deg,#fde8e8,#fca5a5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 28, margin: '0 auto 18px',
          boxShadow: '0 4px 14px rgba(220,38,38,.2)',
        }}>
          📵
        </div>

        {/* Título */}
        <h3 style={{ margin: '0 0 8px', fontSize: 17, fontWeight: 800, color: '#1a1a2e' }}>
          ¿Finalizar el llamado?
        </h3>

        {/* Nombre del incidente */}
        <p style={{
          margin: '0 0 8px',
          fontSize: 13, fontWeight: 700,
          color: '#c0392b',
          background: '#fde8e8',
          borderRadius: 8, padding: '6px 12px',
          display: 'inline-block',
        }}>
          &ldquo;{titulo}&rdquo;
        </p>

        {/* Advertencia */}
        <p style={{ margin: '12px 0 0', fontSize: 12.5, color: '#6c757d', lineHeight: 1.6 }}>
          El incidente quedará <strong>cerrado</strong> y toda la información se conservará en el historial.
        </p>

        {/* Botones */}
        <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
          <button
            onClick={onCancel}
            disabled={loading}
            style={{
              flex: 1, padding: '11px 0', borderRadius: 10,
              border: '1.5px solid #dee2e6', background: '#fff',
              color: '#495057', fontWeight: 700, fontSize: 13,
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'all .15s',
            }}
          >
            Cancelar
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            style={{
              flex: 1, padding: '11px 0', borderRadius: 10,
              border: 'none',
              background: loading
                ? '#e2e6ea'
                : 'linear-gradient(135deg,#c0392b,#e74c3c)',
              color: loading ? '#9aa4b2' : '#fff',
              fontWeight: 800, fontSize: 13,
              cursor: loading ? 'not-allowed' : 'pointer',
              boxShadow: loading ? 'none' : '0 4px 12px rgba(192,57,43,.4)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              transition: 'all .15s',
            }}
          >
            {loading ? '⏳ Finalizando…' : '✅ Confirmar'}
          </button>
        </div>
      </div>
    </div>
  );
}

ConfirmModal.propTypes = {
  titulo:    PropTypes.string.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onCancel:  PropTypes.func.isRequired,
  loading:   PropTypes.bool,
};

/* ── Card de incidente ───────────────────────────────────────────────────── */
function IncCard({ inc, respuestasInc, onFinalizar }) {
  const [finalizando, setFinalizando] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  const prioridad = (inc.Prioridad || inc.prioridad || 'media').toLowerCase();
  const prCfg = PRIORIDAD[prioridad] || PRIORIDAD.media;
  const fecha = new Date(inc.createdAt || inc.Fecha_Servicio || inc.fechaInicio).toLocaleString('es-GT', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  });

  const noCtrl = inc.noControl ?? inc.NoControl;
  const titulo =
    inc.Observaciones?.trim().substring(0, 60) ||
    inc.descripcion?.trim().substring(0, 60) ||
    (inc.Nombre_Solicitante?.trim() ? `Solicita: ${inc.Nombre_Solicitante.trim()}` : null) ||
    (noCtrl ? `Incidente #${noCtrl}` : `Incidente #${String(inc.id).substring(0, 8)}…`);

  const enCamino   = respuestasInc.filter(r => r.estado === 'en_camino' || r.estado === 'en_camino_compania').length;
  const enEscena   = respuestasInc.filter(r => r.estado === 'en_escena' || r.estado === 'en_camino_escena').length;
  const enCompania = respuestasInc.filter(r => r.estado === 'en_compania').length;
  const disponible = respuestasInc.filter(r => r.estado === 'disponible').length;
  const hayResp    = respuestasInc.length > 0;

  const handleFinalizar = () => setShowConfirm(true);

  const confirmarFinalizar = async () => {
    setFinalizando(true);
    try {
      await onFinalizar(inc);
    } finally {
      if (mountedRef.current) { setFinalizando(false); setShowConfirm(false); }
    }
  };

  return (
    <div style={{
      background: '#fff',
      borderRadius: 14,
      marginBottom: 14,
      borderTop: `3px solid ${prCfg.border}`,
      boxShadow: '0 2px 10px rgba(0,0,0,.06)',
      overflow: 'hidden',
    }}>
      {/* ─ Cabecera ─ */}
      <div style={{ padding: '14px 18px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: '#1a1a2e', marginBottom: 5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {titulo}
          </div>
          <div style={{ fontSize: 12, color: '#6c757d', marginBottom: 2 }}>
            📍 {inc.Direccion || inc.direccion || '—'}
          </div>
          <div style={{ fontSize: 11, color: '#adb5bd' }}>🕐 {fecha}</div>
        </div>
        {/* Badges derecha */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 5, flexShrink: 0 }}>
          <span style={{
            padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
            background: prCfg.bg, color: prCfg.color, border: `1px solid ${prCfg.border}40`,
          }}>
            {prCfg.label}
          </span>
          <span style={{
            padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
            background: hayResp ? '#e8f0fe' : '#f0f2f5',
            color: hayResp ? '#1a56db' : '#9aa4b2',
            border: hayResp ? '1px solid #c7d9fa' : '1px solid #e2e6ea',
          }}>
            👥 {respuestasInc.length} {respuestasInc.length === 1 ? 'respuesta' : 'respuestas'}
          </span>
        </div>
      </div>

      {/* ─ Mini resumen de estados ─ */}
      {hayResp && (
        <div style={{ padding: '0 18px 10px', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {enCamino  > 0 && <span style={{ fontSize: 11, fontWeight: 600, background: '#fff3e0', color: '#b45309', borderRadius: 20, padding: '2px 9px', border: '1px solid #fcd34d80' }}>🚒 {enCamino} en camino</span>}
          {enCompania > 0 && <span style={{ fontSize: 11, fontWeight: 600, background: '#eafaf1', color: '#1a7a3c', borderRadius: 20, padding: '2px 9px', border: '1px solid #6fcf9780' }}>🏠 {enCompania} en compañía</span>}
          {enEscena  > 0 && <span style={{ fontSize: 11, fontWeight: 600, background: '#fef2f2', color: '#b91c1c', borderRadius: 20, padding: '2px 9px', border: '1px solid #fca5a580' }}>🔥 {enEscena} en escena</span>}
          {disponible > 0 && <span style={{ fontSize: 11, fontWeight: 600, background: '#f0fdf4', color: '#15803d', borderRadius: 20, padding: '2px 9px', border: '1px solid #86efac80' }}>✅ {disponible} disponible</span>}
        </div>
      )}

      {/* ─ Lista de respuestas ─ */}
      <div style={{ borderTop: '1px solid #f1f3f5', padding: '12px 18px 14px', display: 'flex', flexDirection: 'column', gap: 7 }}>
        {hayResp ? (
          respuestasInc.map((r, i) => <BomberoRow key={r.id || i} r={r} />)
        ) : (
          <div style={{
            color: '#ced4da', fontSize: 12, textAlign: 'center', padding: '14px 0',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          }}>
            <span style={{ fontSize: 16 }}>⏳</span> Esperando respuestas de bomberos…
          </div>
        )}
      </div>

      {/* ─ Botón Finalizar llamado (solo encargados) ─ */}
      {onFinalizar && (
        <div style={{ padding: '0 18px 16px' }}>
          <button
            onClick={handleFinalizar}
            disabled={finalizando}
            style={{
              width: '100%',
              background: finalizando ? '#e2e6ea' : 'linear-gradient(135deg, #c0392b, #e74c3c)',
              color: finalizando ? '#9aa4b2' : '#fff',
              border: 'none',
              borderRadius: 10,
              padding: '11px 0',
              fontSize: 13,
              fontWeight: 800,
              cursor: finalizando ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              boxShadow: finalizando ? 'none' : '0 3px 10px rgba(192,57,43,.35)',
              transition: 'all .2s',
            }}
          >
            {finalizando ? (
              <><span style={{ fontSize: 15 }}>⏳</span> Finalizando…</>
            ) : (
              <><span style={{ fontSize: 15 }}>📵</span> Finalizar llamado</>
            )}
          </button>
        </div>
      )}

      {/* ─ Modal confirmación ─ */}
      {showConfirm && (
        <ConfirmModal
          titulo={titulo}
          onConfirm={confirmarFinalizar}
          onCancel={() => setShowConfirm(false)}
          loading={finalizando}
        />
      )}
    </div>
  );
}

IncCard.propTypes = {
  inc: PropTypes.shape({
    id:                 PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    noControl:          PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    NoControl:          PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    Prioridad:          PropTypes.string,
    prioridad:          PropTypes.string,
    Observaciones:      PropTypes.string,
    descripcion:        PropTypes.string,
    Nombre_Solicitante: PropTypes.string,
    Direccion:          PropTypes.string,
    direccion:          PropTypes.string,
    createdAt:          PropTypes.string,
    Fecha_Servicio:     PropTypes.string,
    fechaInicio:        PropTypes.string,
  }).isRequired,
  respuestasInc: PropTypes.arrayOf(PropTypes.shape({
    id:     PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    nombre: PropTypes.string,
    carnet: PropTypes.string,
    estado: PropTypes.string,
  })).isRequired,
  onFinalizar: PropTypes.func,
};

/* ── Pantalla principal ──────────────────────────────────────────────────── */
export default function Respuestas() {
  const { socket } = useSocket();
  const { sesion, esBombero } = useAuth();
  const [incidentes,  setIncidentes]  = useState([]);
  const [respuestas,  setRespuestas]  = useState({});
  const [cargando,    setCargando]    = useState(true);
  const [error,       setError]       = useState('');
  const [refreshing,  setRefreshing]  = useState(false);

  const agregarRespuesta = (sid, data) => {
    setRespuestas(prev => {
      const key  = String(sid);
      const list = prev[key] || [];
      const idx  = list.findIndex(r => r.carnet === data.carnet);
      const next = idx >= 0 ? list.map((r, i) => i === idx ? { ...r, ...data } : r) : [...list, data];
      return { ...prev, [key]: next };
    });
  };

  // Busca respuestas por el id del incidente Y por su NoControl (por si el bombero respondió con clave distinta)
  const getRespuestas = useMemo(() => (inc) => {
    const byId    = respuestas[String(inc.id)] || [];
    const noCtrl  = String(inc.noControl ?? inc.NoControl ?? '');
    const byNoCtrl = noCtrl && noCtrl !== String(inc.id) ? (respuestas[noCtrl] || []) : [];
    if (byNoCtrl.length === 0) return byId;
    const merged = [...byId];
    byNoCtrl.forEach(r => { if (!merged.find(m => m.carnet === r.carnet)) merged.push(r); });
    return merged;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [respuestas]);

  const cargar = useCallback(async () => {
    setError('');
    setRefreshing(true);
    try {
      const codCompania = sesion?.cod_compania?.[0] ? Number(sesion.cod_compania[0]) : 44;
      // Mismo rango que Despacho: medianoche local de hace 30 días
      const ini = new Date(ini2Str() + 'T00:00:00').toISOString();
      const fin = new Date().toISOString();

      const [rAzure, rLocal, rResp] = await Promise.allSettled([
        getListadoServiciosGeo(codCompania, ini, fin, ''),
        api.get('/servicios'),
        api.get('/respuestas'),
      ]);

      const azureList = rAzure.status === 'fulfilled' ? (rAzure.value.data || []) : [];
      const localList = rLocal.status === 'fulfilled' ? (rLocal.value.data || []) : [];

      // Siempre mostrar Azure como fuente principal + locales sin match en Azure
      const azureNoControls = new Set(azureList.map(s => String(s.NoControl)));

      const azureIncidents = azureList.map(s => {
        const local = localList.find(l => l.NoControl != null && String(l.NoControl) === String(s.NoControl));
        return {
          ...s,
          id:          local?.id ?? String(s.NoControl),
          descripcion: s.Observaciones || '',
          direccion:   s.Direccion,
          prioridad:   COD_PRIORIDAD[String(s.Cod_Prioridad)] || 'media',
          fechaInicio: s.Fecha_Servicio,
          estado:      local?.estado ?? 'pendiente',
        };
      });

      // Incidentes locales sin NoControl en Azure (creados localmente, Azure falló o aún no tiene NoControl)
      const localOnly = localList
        .filter(l => !l.NoControl || !azureNoControls.has(String(l.NoControl)))
        .map(l => ({
          ...l,
          id:          l.id,
          descripcion: l.Observaciones || '',
          direccion:   l.Direccion,
          prioridad:   l.Prioridad?.toLowerCase() || 'media',
          fechaInicio: l.Fecha_Servicio,
          estado:      l.estado ?? 'pendiente',
        }));

      setIncidentes([...azureIncidents, ...localOnly]);

      if (rResp.status === 'fulfilled') {
        const map = {};
        (rResp.value.data || []).forEach(r => {
          const key = String(r.servicioId);
          if (!map[key]) map[key] = [];
          map[key].push(r);
        });
        setRespuestas(map);
      }
    } catch {
      setError('No se pudieron cargar los datos.');
    } finally {
      setCargando(false);
      setRefreshing(false);
    }
  }, [sesion]);

  useEffect(() => { cargar(); }, [cargar]);

  // Escucha socket: incidente cerrado por otro usuario
  useEffect(() => {
    if (!socket) return;
    const onNuevo = inc => setIncidentes(prev => [inc, ...prev]);
    const onResp  = data => { if (data.servicioId) agregarRespuesta(data.servicioId, data); };
    const onActualizado = inc => {
      if (inc.id) setIncidentes(prev => prev.map(i => i.id === inc.id ? { ...i, ...inc } : i));
    };
    socket.on('nuevo_incidente',    onNuevo);
    socket.on('respuesta_bombero',  onResp);
    socket.on('incidente_actualizado', onActualizado);
    return () => {
      socket.off('nuevo_incidente',    onNuevo);
      socket.off('respuesta_bombero',  onResp);
      socket.off('incidente_actualizado', onActualizado);
    };
  }, [socket]);

  const finalizarLlamado = async (inc) => {
    try {
      await api.patch(`/servicios/${inc.id}/estado`, { estado: 'cerrado' });
    } catch (err) {
      if (err.response?.status === 404) {
        // Incidente solo-Azure sin registro local: crear registro local con estado cerrado
        // para que persista después de recargar (Azure no se toca)
        await api.post('/servicios', {
          NoControl:          inc.NoControl ?? inc.noControl,
          Cod_Compania:       sesion?.cod_compania?.[0] ? Number(sesion.cod_compania[0]) : 44,
          Cod_Servicio:       inc.Cod_Servicio,
          Fecha_Servicio:     inc.Fecha_Servicio || inc.fechaInicio,
          Nombre_Solicitante: inc.Nombre_Solicitante,
          Observaciones:      inc.Observaciones || inc.descripcion,
          Direccion:          inc.Direccion || inc.direccion || '—',
          Latitud:            inc.Latitud,
          Longitud:           inc.Longitud,
          Prioridad:          inc.prioridad || 'media',
          estado:             'cerrado',
        });
      } else {
        throw err;
      }
    }
    setIncidentes(prev => prev.map(i => i.id === inc.id ? { ...i, estado: 'cerrado' } : i));
  };

  const pendientes  = incidentes.filter(i => i.estado !== 'cerrado');
  const conRespuesta = pendientes.filter(i => getRespuestas(i).length > 0);
  const todasResp   = conRespuesta.flatMap(i => getRespuestas(i));
  const enCamino    = todasResp.filter(r => r.estado === 'en_camino' || r.estado === 'en_camino_compania').length;
  const enEscena    = todasResp.filter(r => r.estado === 'en_escena' || r.estado === 'en_camino_escena').length;
  const conResp     = conRespuesta.length;

  if (cargando) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
        <div style={{ textAlign: 'center', color: '#888' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🚒</div>
          <div>Cargando respuestas…</div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* ─ Estadísticas ─ */}
      <div style={{ display: 'flex', gap: 14, marginBottom: 24, flexWrap: 'wrap' }}>
        {[
          { label: 'Con respuestas',  value: conResp,   color: '#1a56db', bg: '#e8f0fe', emoji: '💬' },
          { label: 'En camino',       value: enCamino,  color: '#b45309', bg: '#fff3e0', emoji: '🚒' },
          { label: 'En escena',       value: enEscena,  color: '#b91c1c', bg: '#fef2f2', emoji: '🔥' },
        ].map(s => (
          <div key={s.label} style={{
            flex: '1 1 180px',
            background: '#fff', borderRadius: 14,
            padding: '18px 20px',
            boxShadow: '0 1px 6px rgba(0,0,0,.06)',
            borderTop: `4px solid ${s.color}`,
          }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#9aa4b2', textTransform: 'uppercase', letterSpacing: '.8px', marginBottom: 8 }}>
              {s.label}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 28, lineHeight: 1 }}>{s.emoji}</span>
              <span style={{ fontSize: 32, fontWeight: 800, color: s.color, lineHeight: 1 }}>{s.value}</span>
            </div>
          </div>
        ))}
      </div>

      {/* ─ Barra de acciones ─ */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: '#1a1a2e', margin: 0 }}>
          Respuestas por incidente · <span style={{ color: '#8a9bb0', fontWeight: 600 }}>Compañía 44</span>
        </h2>
        <button onClick={cargar} disabled={refreshing} style={{ background: '#1a1a2e', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontSize: 12, fontWeight: 700, opacity: refreshing ? 0.6 : 1 }}>
          {refreshing ? '⏳ Actualizando…' : '🔄 Actualizar'}
        </button>
      </div>

      {error && (
        <div style={{ background: '#fde8e8', border: '1px solid #f5c6c6', borderRadius: 10, padding: '12px 16px', marginBottom: 16, color: '#c0392b', fontSize: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          ⚠️ {error}
          <button onClick={cargar} style={{ background: '#c0392b', color: '#fff', border: 'none', borderRadius: 6, padding: '4px 12px', cursor: 'pointer', fontSize: 12 }}>Reintentar</button>
        </div>
      )}

      {conRespuesta.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', background: '#fff', borderRadius: 14, boxShadow: '0 2px 8px rgba(0,0,0,.06)' }}>
          <div style={{ fontSize: 52, marginBottom: 12 }}>⏳</div>
          <div style={{ fontSize: 17, fontWeight: 700, color: '#1a1a2e', marginBottom: 6 }}>Sin respuestas aún</div>
          <div style={{ fontSize: 14, color: '#7f8c8d' }}>Los incidentes aparecerán aquí cuando un bombero responda.</div>
        </div>
      ) : (
        /* Solo incidentes con al menos 1 respuesta, más recientes primero */
        [...conRespuesta].sort((a, b) => {
          const da = new Date(a.createdAt || a.Fecha_Servicio || a.fechaInicio || 0);
          const db = new Date(b.createdAt || b.Fecha_Servicio || b.fechaInicio || 0);
          return db - da;
        }).map(inc => (
          <IncCard key={inc.id} inc={inc} respuestasInc={getRespuestas(inc)} onFinalizar={!esBombero() ? finalizarLlamado : undefined} />
        ))
      )}
    </div>
  );
}
